// News & Sentiment — headline feed (Phase 3).
//
// Emits the SAME DOM + class names as the legacy renderNewsSentimentModule, so
// the shared CSS renders it identically (verified by the visual A/B test). Data
// comes from app-api via `useWidgetData`; the team control stays a
// legacy shell select and its value arrives through the bridge context (`team`,
// defaulting to "Arsenal", matching the legacy `teamVal || 'Arsenal'`).
//
// Each item shows a sentiment bar (green positive / red negative) then the
// headline + time. No "updated" stamp and no empty-state text — the legacy
// renderer just maps items, so an empty feed renders an empty wrapper.

import { EMPTY_NEWS, newsBarColor } from "@bento/shared";
import { useWidgetData } from "../../data/useWidgetData.js";
import { useFixtureKey } from "../../data/useFixtureKey.js";
import WidgetSkeleton from "./WidgetSkeleton.js";
import WidgetError from "./WidgetError.js";

export interface NewsSentimentProps {
  /** Active fixture label; slugified for the endpoint. */
  fixture: string;
  /** Selected team (full name), from the team-toggle; falls back to Arsenal. */
  team?: string | null;
}

export default function NewsSentiment({ fixture, team }: NewsSentimentProps) {
  const selected = team || "Arsenal";
  const {
    data: news,
    isPending,
    isError,
    refetch,
  } = useWidgetData("news-sentiment", useFixtureKey(fixture));

  if (isPending) return <WidgetSkeleton />;
  if (isError) return <WidgetError onRetry={refetch} />;

  const data = news[selected] ?? EMPTY_NEWS;

  return (
    <div className="data-wrapper top-align ns-top-pad">
      {data.items.map((it, i) => (
        <div className="ns-item" key={`${it.time}-${i}`}>
          <div
            className="ns-item-bar"
            style={{ background: newsBarColor(it.pos) }}
          />
          <div>
            <div className="ns-item-text">{it.text}</div>
            <div className="ns-item-time">{it.time}</div>
          </div>
        </div>
      ))}
    </div>
  );
}
